/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
*/
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {
  PageState
} from '@ion/applications/src/types/pages';
import {
  AdminAction,
  AsyncState
} from '@ion/applications/src/types/redux';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  PegLabel,
  PegLabels,
  PegLabelsFilters
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {
  ADD_ADMIN_PEG_LABEL_ERROR,
  ADD_ADMIN_PEG_LABEL_REQUEST,
  ADD_ADMIN_PEG_LABEL_RESPONSE,
  ADMIN_PEG_LABELS_GRID_REFRESHED,
  AUDIT_ADMIN_PEG_LABEL,
  AUDIT_ADMIN_PEG_LABEL_SHOW_CHANGES_DIALOG,
  DELETE_ADMIN_PEG_LABELS_ERROR,
  DELETE_ADMIN_PEG_LABELS_REQUEST,
  DELETE_ADMIN_PEG_LABELS_RESPONSE,
  DISPLAY_DELETE_PEG_LABELS_DIALOG,
  EDIT_ADMIN_PEG_LABEL_ERROR,
  EDIT_ADMIN_PEG_LABEL_REQUEST,
  EDIT_ADMIN_PEG_LABEL_RESPONSE,
  GET_ADMIN_PEG_LABELS_ERROR,
  GET_ADMIN_PEG_LABELS_REQUEST,
  GET_ADMIN_PEG_LABELS_RESPONSE,
  INITIALIZE_ADMIN_PEG_LABELS,
  RESET_PEG_LABELS_SEARCH_QUERIES,
  SET_ADMIN_PEG_LABELS_GRID_COLUMNS,
  SET_GRID_PAGE_SIZE,
  SHOW_ADD_PEG_LABEL_DIALOG,
  SHOW_EDIT_PEG_LABEL_DIALOG,
  UPDATE_ADMIN_PEG_LABELS_GRID_COLUMNS,
  UPDATE_PEG_LABELS_SEARCH_QUERIES
} from '../actions/admin-peglabels';

// Defines the user preference key for the Admin Peg Labels page
export const PREFERENCE = 'admin-peglabels';

export interface AdminPegLabelsAction extends AdminAction {
  pegLabels?: PegLabel | PegLabels;
  searchParams?: PegLabelsFilters;
  showDialog?: boolean;
  isView?: boolean;
};

export interface PegLabelsAuditState extends PegLabelsActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

export interface AdminPegLabelsDestroyState extends AsyncState {
  selected: PegLabel | PegLabels | null;
  result: PegLabel | PegLabels | null;
  showDialog: boolean;
};

interface PegLabelsActionState extends AsyncState {
  selected: PegLabel | PegLabels | null;
  result: PegLabel | PegLabels | null;
  showDialog: boolean;
  isView?: boolean;
};

export interface AdminPegLabelsState extends PageState {
  pegLabels: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    pageSize: number
  }
  destroy: AdminPegLabelsDestroyState,
  search: PegLabelsFilters,
  add: PegLabelsActionState,
  edit: PegLabelsActionState,
  audit: PegLabelsAuditState
};

export const initialState: AdminPegLabelsState = {
  isInitialized : false,
  pegLabels     : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    pageSize            : gridDefaultPageSize
  },
  destroy : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  search : {
    name : null,
    type : null
  },
  add : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  edit : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isView     : false
  },
  audit : {
    showDialog        : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  }
};

const reducers = {
  [INITIALIZE_ADMIN_PEG_LABELS] : (draft: AdminPegLabelsState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.destroy.error            = null;
    draft.search.name = draft.search.type = null;
  },
  [ADMIN_PEG_LABELS_GRID_REFRESHED] : ({destroy, add}: AdminPegLabelsState) => {
    destroy.result = add.result = null;
    destroy.error  = add.result = null;
  },
  [SET_ADMIN_PEG_LABELS_GRID_COLUMNS] : ({grid, pegLabels}: AdminPegLabelsState, {columnState}) => {
    onSetGridColumns({
      isLoading  : pegLabels.loading,
      preference : PREFERENCE,
      grid
    }, columnState);
  },
  [UPDATE_ADMIN_PEG_LABELS_GRID_COLUMNS] : ({grid}: AdminPegLabelsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_ADMIN_PEG_LABELS_REQUEST] : ({pegLabels}: AdminPegLabelsState) => {
    pegLabels.loading = true;
  },
  [GET_ADMIN_PEG_LABELS_RESPONSE] : ({pegLabels}: AdminPegLabelsState) => {
    pegLabels.loading = false;
  },
  [GET_ADMIN_PEG_LABELS_ERROR] : ({pegLabels}: AdminPegLabelsState, {error}: AdminPegLabelsAction) => {
    pegLabels.loading = false;
    pegLabels.error   = error;
  },
  [DISPLAY_DELETE_PEG_LABELS_DIALOG] : ({destroy}: AdminPegLabelsState, {pegLabels, show}: AdminPegLabelsAction) => {
    destroy.selected = show ? pegLabels : null;
    destroy.showDialog = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_PEG_LABELS_REQUEST] : ({destroy}: AdminPegLabelsState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_PEG_LABELS_RESPONSE] : ({destroy}: AdminPegLabelsState, {pegLabels}: AdminPegLabelsAction) => {
    destroy.result = pegLabels;
    destroy.selected = null;
    destroy.loading = destroy.showDialog = false;
  },
  [DELETE_ADMIN_PEG_LABELS_ERROR] : ({destroy}: AdminPegLabelsState, {error}: AdminPegLabelsAction) => {
    destroy.error = error;
    destroy.result = null;
    destroy.loading = destroy.showDialog = false;
  },
  [UPDATE_PEG_LABELS_SEARCH_QUERIES] : ({search}: AdminPegLabelsState, {searchParams}: AdminPegLabelsAction) => {
    search.name = searchParams.name ?? null;
    search.type = searchParams.type ?? null;
  },
  [RESET_PEG_LABELS_SEARCH_QUERIES] : ({search}: AdminPegLabelsState) => {
    search.name = null;
    search.type = null;
  },
  [SHOW_ADD_PEG_LABEL_DIALOG] : ({add}: AdminPegLabelsState, {showDialog}: AdminPegLabelsAction) => {
    add.showDialog = showDialog;
  },
  [ADD_ADMIN_PEG_LABEL_REQUEST] : ({add}: AdminPegLabelsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_PEG_LABEL_RESPONSE] : ({add}: AdminPegLabelsState, {pegLabels}: AdminPegLabelsAction) => {
    add.result = pegLabels;
    add.selected = null;
    add.loading = add.showDialog = false;
  },
  [ADD_ADMIN_PEG_LABEL_ERROR] : ({add}: AdminPegLabelsState, {error}: AdminPegLabelsAction) => {
    add.error = error;
    add.result = null;
    add.loading = add.showDialog = false;
  },
  [SHOW_EDIT_PEG_LABEL_DIALOG] : (
    {edit}: AdminPegLabelsState,
    {showDialog, pegLabels, isView}: AdminPegLabelsAction
  ) => {
    edit.selected      = showDialog ? pegLabels : null;
    edit.result        = null;
    edit.showDialog    = showDialog;
    edit.isView        = isView;
  },
  [EDIT_ADMIN_PEG_LABEL_REQUEST] : ({edit}: AdminPegLabelsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_PEG_LABEL_RESPONSE] : ({edit}: AdminPegLabelsState, {pegLabels}: AdminPegLabelsAction) => {
    edit.result = pegLabels;
    edit.selected = null;
    edit.loading = edit.showDialog = false;
  },
  [EDIT_ADMIN_PEG_LABEL_ERROR] : ({edit}: AdminPegLabelsState, {error}: AdminPegLabelsAction) => {
    edit.error = error;
    edit.result = edit.selected = null;
    edit.loading = edit.showDialog = false;
  },
  [AUDIT_ADMIN_PEG_LABEL] : ({audit}: AdminPegLabelsState, {pegLabels, show}: AdminPegLabelsAction) => {
    audit.selected   = show ? pegLabels : null;
    audit.showDialog = show;
  },
  [AUDIT_ADMIN_PEG_LABEL_SHOW_CHANGES_DIALOG] : ({audit}: AdminPegLabelsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminPegLabelsState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

export default produce((draft: AdminPegLabelsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
